// contexts/OrganizationContext.jsx
import React, { createContext, useState, useEffect, useContext } from 'react';
import OrganizationService from '../services/organization.service';
import AccountService from '../services/account.service';
import ProjectService from '../services/project.service';
import { useAuth } from '../useAuth';

/**
 * Organization Context
 * Maneja el estado global de organizaciones, cuentas y proyectos
 */
const OrganizationContext = createContext();

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within OrganizationProvider');
  }
  return context;
};

export const OrganizationProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  
  // Estado de organizaciones
  const [organizations, setOrganizations] = useState([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  
  // Estado de cuentas
  const [personalAccount, setPersonalAccount] = useState(null);
  const [currentAccount, setCurrentAccount] = useState(() => {
    // Intentar cargar cuenta persistida al inicio
    try {
      const savedAccountId = localStorage.getItem('currentAccountId');
      const savedAccountType = localStorage.getItem('currentAccountType');
      if (savedAccountId && savedAccountType) {
        return {
          id: savedAccountId,
          type: savedAccountType,
          _restored: true // Flag para saber que se restauró
        };
      }
    } catch {
      // Ignore error
    }
    return null;
  });
  const [accountsLoading, setAccountsLoading] = useState(false);
  
  // Estado de proyectos
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  
  // Contexto actual (PERSONAL o ORGANIZATION)
  const [currentContext, setCurrentContext] = useState(() => {
    // Intentar cargar desde localStorage al inicio
    try {
      const saved = localStorage.getItem('currentContext');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  
  // Errores
  const [error, setError] = useState(null);

  /**
   * Persistir contexto actual en localStorage
   */
  const persistCurrentContext = (context) => {
    try {
      if (context) {
        localStorage.setItem('currentContext', JSON.stringify(context));
      } else {
        localStorage.removeItem('currentContext');
      }
    } catch (error) {
      console.warn('Failed to persist context to localStorage:', error);
    }
  };

  /**
   * Persistir la cuenta actual para restauración
   */
  const persistCurrentAccount = (account) => {
    try {
      if (account && account.id) {
        localStorage.setItem('currentAccountId', account.id);
        localStorage.setItem('currentAccountType', account.type || 'PERSONAL');
      } else {
        localStorage.removeItem('currentAccountId');
        localStorage.removeItem('currentAccountType');
      }
    } catch (error) {
      console.warn('Failed to persist current account to localStorage:', error);
    }
  };

  /**
   * Cargar cuenta personal del usuario
   */
  const loadPersonalAccount = async () => {
    if (!isAuthenticated || !user) return;
    setAccountsLoading(true);
    const result = await AccountService.getPersonalAccount();
    if (result.success) {
      setPersonalAccount(result.data);
      
      // Solo configurar como contexto por defecto si no hay contexto guardado
      if (!currentContext) {
        const defaultContext = {
          type: 'PERSONAL',
          id: result.data.id,
          name: 'Personal'
        };
        setCurrentContext(defaultContext);
        setCurrentAccount(result.data);
        persistCurrentContext(defaultContext);
      } else if (currentContext.type === 'PERSONAL') {
        // Si el contexto guardado es personal, asegurar que currentAccount esté configurado
        setCurrentAccount(result.data);
      }
    } else {
      setError(result.error);
    }
    setAccountsLoading(false);
  };

  /**
   * Cargar organizaciones del usuario
   */
  const loadOrganizations = async () => {
    if (!isAuthenticated) return;
    setOrganizationsLoading(true);
    const result = await OrganizationService.listOrganizations();
    if (result.success) {
      setOrganizations(result.data);
    } else {
      setError(result.error);
      setOrganizations([]);
    }
    setOrganizationsLoading(false);
  };

  /**
   * Cargar proyectos según el contexto actual
   */
  const loadProjects = async () => {
    if (!isAuthenticated || !currentAccount) return;
    
    // Loading projects
    setProjectsLoading(true);
    
    // Clear projects immediately to avoid showing wrong projects
    setProjects([]);
    
    let result;
    if (currentContext?.type === 'PERSONAL') {
      // Para cuenta personal, usar el account_id de la cuenta personal
      result = await ProjectService.listPersonalProjects({ accountId: currentAccount.id });
    } else if (currentContext?.type === 'ORGANIZATION') {
      // Para organización, usar el account_id de la organización (no el organization_id)
      result = await ProjectService.listOrganizationProjects(currentAccount.id);
    }
    
    if (result?.success) {
      // Projects loaded
      setProjects(result.data);
    } else {
      console.error('❌ Failed to load projects:', result?.error);
      setError(result?.error);
      setProjects([]);
    }
    setProjectsLoading(false);
  };

  /**
   * Crear nueva organización
   */
  const createOrganization = async (organizationData) => {
    setOrganizationsLoading(true);
    const result = await OrganizationService.createOrganization(organizationData);
    
    if (result.success) {
      // Recargar organizaciones
      await loadOrganizations();
    } else {
      setError(result.error);
    }
    setOrganizationsLoading(false);
    return result;
  };

  /**
   * Actualizar organización
   */
  const updateOrganization = async (organizationId, updates) => {
    const result = await OrganizationService.updateOrganization(organizationId, updates);
    
    if (result.success) {
      // Actualizar en el estado local
      setOrganizations(orgs => 
        orgs.map(org => 
          org.id === organizationId ? { ...org, ...updates } : org
        )
      );
    } else {
      setError(result.error);
    }
    return result;
  };

  /**
   * Eliminar organización
   */
  const deleteOrganization = async (organizationId) => {
    const result = await OrganizationService.deleteOrganization(organizationId);
    
    if (result.success) {
      // Remover del estado local
      setOrganizations(orgs => orgs.filter(org => org.id !== organizationId));
      
      // Si era el contexto actual, cambiar a personal
      if (currentContext?.type === 'ORGANIZATION' && currentContext?.id === organizationId) {
        changeContext({ type: 'PERSONAL', id: personalAccount.id });
      }
    } else {
      setError(result.error);
    }
    return result;
  };

  /**
   * Cambiar contexto actual (Personal <-> Organization)
   * Cuando se selecciona una organización, se recarga la página virtualmente:
   * - Se obtiene la información completa de la organización
   * - Se cambia el account ID activo
   * - Se recargan todos los recursos (assistants, avatars, projects, etc.)
   */
  const changeContext = async (newContext) => {
    setError(null);
    
    try {
      if (newContext.type === 'PERSONAL') {
        setCurrentContext(newContext);
        setCurrentAccount(personalAccount);
        persistCurrentContext(newContext);
        persistCurrentAccount(personalAccount);
      } else if (newContext.type === 'ORGANIZATION') {
        
        // 1. Obtener información completa de la organización
        const orgResult = await OrganizationService.getOrganization(newContext.id);
        if (!orgResult.success) {
          setError(orgResult.error);
          return;
        }
        
        // 2. Actualizar el contexto con la información completa
        const updatedContext = {
          ...newContext,
          name: orgResult.data.organization?.name || orgResult.data.organization_name,
          domain: orgResult.data.organization?.domain,
          accountType: 'ORGANIZATION'
        };
        setCurrentContext(updatedContext);
        persistCurrentContext(updatedContext);
        
        // 3. Usar el organizational_account_id (ahora accesible directamente)
        const organizationalAccountId = orgResult.data.organizational_account_id;
        
        if (!organizationalAccountId) {
          setError(['No organizational account ID found']);
          return;
        }
        
        const newAccount = {
          ...orgResult.data.organization, // Spread primero
          id: organizationalAccountId, // ✅ Luego sobrescribir con el account ID correcto
          name: orgResult.data.organization?.name || orgResult.data.organization_name,
          type: 'ORGANIZATION',
          organization_id: orgResult.data.organization_id || orgResult.data.organization?.id,
        };
        
        setCurrentAccount(newAccount);
        persistCurrentAccount(newAccount);
      }
      
    } catch (error) {
      setError(['Failed to switch context: ' + error.message]);
    }
  };

  /**
   * Crear proyecto
   */
  const createProject = async (projectData) => {
    setProjectsLoading(true);
    
    // Preparar los datos del proyecto según el contexto
    const payload = { ...projectData };
    
    if (currentContext?.type === 'ORGANIZATION') {
      // Para organizaciones, usar el currentAccount.id (que es el organizational_account_id)
      // Y también incluir organization_id
      payload.account_id = currentAccount?.id;
      payload.organization_id = currentContext.id;
    } else if (currentContext?.type === 'PERSONAL') {
      // Para proyectos personales, usar account_id personal
      payload.account_id = currentAccount?.id;
      // Remover organization_id para proyectos personales
      delete payload.organization_id;
    } else {
      setError(['Invalid context for project creation']);
      setProjectsLoading(false);
      return { success: false, error: ['Invalid context'] };
    }
    
    const result = await ProjectService.createProject(payload);
    
    if (result.success) {
      setProjects(prev => [...prev, result.data]);
    } else {
      setError(result.error);
    }
    setProjectsLoading(false);
    return result;
  };

  /**
   * Actualizar proyecto
   */
  const updateProject = async (projectId, updates) => {
    const result = await ProjectService.updateProject(projectId, updates);
    
    if (result.success) {
      setProjects(projects => 
        projects.map(p => 
          p.id === projectId ? { ...p, ...updates } : p
        )
      );
    } else {
      setError(result.error);
    }
    return result;
  };

  /**
   * Eliminar proyecto
   */
  const deleteProject = async (projectId) => {
    const result = await ProjectService.deleteProject(projectId);
    
    if (result.success) {
      setProjects(projects => projects.filter(p => p.id !== projectId));
    } else {
      setError(result.error);
    }
    return result;
  };

  /**
   * Agregar miembro a organización
   */
  const addMember = async (organizationId, memberData) => {
    const result = await OrganizationService.addMember(organizationId, memberData);
    if (!result.success) {
      setError(result.error);
    }
    return result;
  };

  /**
   * Remover miembro de organización
   */
  const removeMember = async (organizationId, userId) => {
    const result = await OrganizationService.removeMember(organizationId, userId);
    if (!result.success) {
      setError(result.error);
    }
    return result;
  };

  /**
   * Actualizar rol de miembro
   */
  const updateMemberRole = async (organizationId, userId, newRole) => {
    const result = await OrganizationService.updateMemberRole(organizationId, userId, newRole);
    if (!result.success) {
      setError(result.error);
    }
    return result;
  };

  // Cargar datos iniciales cuando el usuario está autenticado
  useEffect(() => {
    if (isAuthenticated && user) {
      loadPersonalAccount();
      loadOrganizations();
    } else {
      // Limpiar contexto cuando el usuario cierre sesión
      setCurrentContext(null);
      setCurrentAccount(null);
      setPersonalAccount(null);
      setOrganizations([]);
      setProjects([]);
      persistCurrentContext(null);
      persistCurrentAccount(null);
    }
  }, [isAuthenticated, user]);

  // Restaurar contexto de organización después de cargar datos iniciales
  useEffect(() => {
    if (
      isAuthenticated && 
      user && 
      personalAccount && 
      organizations.length >= 0 && // Permitir arrays vacíos también
      currentContext?.type === 'ORGANIZATION' && 
      !currentAccount?.type // Solo si no hemos configurado la cuenta aún
    ) {
      // Restoring organization context
      
      // Buscar la organización en la lista cargada
      const org = organizations.find(o => o.id === currentContext.id);
      if (org) {
        // Restaurar la organización usando changeContext para obtener la cuenta correcta
        changeContext(currentContext).catch(error => {
          console.warn('❌ Failed to restore organization context, falling back to personal:', error);
          // Si falla, cambiar a contexto personal
          const personalContext = {
            type: 'PERSONAL',
            id: personalAccount.id,
            name: 'Personal'
          };
          setCurrentContext(personalContext);
          setCurrentAccount(personalAccount);
          persistCurrentContext(personalContext);
          persistCurrentAccount(personalAccount);
        });
      } else {
        console.warn('⚠️ Organization not found in loaded organizations, switching to personal');
        // Si la organización no existe más, cambiar a personal
        const personalContext = {
          type: 'PERSONAL',
          id: personalAccount.id,
          name: 'Personal'
        };
        setCurrentContext(personalContext);
        setCurrentAccount(personalAccount);
        persistCurrentContext(personalContext);
        persistCurrentAccount(personalAccount);
      }
    }
  }, [isAuthenticated, user, personalAccount, organizations, currentContext?.id]);

  // Cargar proyectos cuando cambia la cuenta (no el contexto para evitar loops)
  useEffect(() => {
    if (currentAccount?.id) {
      // Account changed, reloading projects
      loadProjects();
    } else {
      // No current account
    }
  }, [currentAccount?.id]); // Solo depende del ID de la cuenta

  // Limpiar proyectos cuando no hay cuenta activa
  useEffect(() => {
    if (!currentAccount) {
      setProjects([]);
    }
  }, [currentAccount]);

  const value = {
    // Estado
    organizations,
    organizationsLoading,
    personalAccount,
    currentAccount,
    currentContext,
    projects,
    projectsLoading,
    accountsLoading,
    error,
    
    // Acciones de organizaciones
    createOrganization,
    updateOrganization,
    deleteOrganization,
    loadOrganizations,
    
    // Acciones de contexto
    changeContext,
    
    // Acciones de proyectos
    createProject,
    updateProject,
    deleteProject,
    loadProjects,
    
    // Acciones de miembros
    addMember,
    removeMember,
    updateMemberRole,
    
    // Utilidades
    setError,
    clearError: () => setError(null)
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export default OrganizationContext;
